import React, { useState, useRef, useEffect, useCallback } from 'react'
import { X, Eye, EyeOff } from 'lucide-react'
import { userKanriService } from '../../services/userKanriService'
import { useApiError } from '../../../shared/hooks/useApiError'
import { useBlobUrlFromApi, clearBlobUrlCache } from '../../hooks/useBlobUrl'
import Button from '../../../shared/ui/Button'
import MessageDialog from '../../../shared/ui/MessageDialog'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

// Create a function to generate the schema based on isEdit
const createUserSchema = (isEdit) => z.object({
  userName: z.string().min(1, 'ユーザー名は必須項目です'),
  mailAddress: z.string().min(1, 'メールアドレスは必須項目です').email('有効なメールアドレスを入力してください'),
  loginPassword: z.string().optional(),
  pixtagRiyoFlg: z.boolean(),
  riyoKyotenId: z.string(),
  riyoKahi: z.string().transform((val) => parseInt(val, 10)),
  riyoKengen: z.string().transform((val) => parseInt(val, 10)),
  pixClientUsage: z.union([z.literal(0), z.literal(1)]).optional(),
  pixCameraUsage: z.union([z.literal(0), z.literal(1)]).optional(),
  voiceRecognitionUsage: z.union([z.literal(0), z.literal(1)]).optional(),
  ocrUsage: z.union([z.literal(0), z.literal(1)]).optional(),
  thetaCameraUsage: z.union([z.literal(0), z.literal(1)]).optional()
}).superRefine((data, ctx) => {
  // For new users, password is required
  if (!isEdit && (!data.loginPassword || data.loginPassword.trim() === '')) {
    ctx.addIssue({
      code: 'custom',
      message: 'パスワードは必須項目です',
      path: ['loginPassword']
    })
  }
})

const UserModal = ({
  isOpen,
  onClose,
  onSave,
  user = null,
  isEdit = false,
  userCount = 0,
  riyoPlan = "4",
  companyInfo = null,
  onError
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const [kyotenList, setKyotenList] = useState([])
  const [previewImageUrl, setPreviewImageUrl] = useState('') // For file preview only
  const [selectedImageFile, setSelectedImageFile] = useState(null) // Store selected image file
  const [isLoading, setIsLoading] = useState(false)
  const fileInputRef = useRef(null)

  // Error handling - use parent's error handling if provided, otherwise use local
  const { 
    handleApiError: localHandleApiError, 
    handleApiResponse: localHandleApiResponse,
    showErrorDialog,
    errorMessage,
    closeErrorDialog
  } = useApiError()
  const handleApiError = onError || localHandleApiError
  const handleApiResponse = localHandleApiResponse

  // Use React Hook Form with Zod resolver
  const {
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createUserSchema(isEdit)),
    mode: 'onSubmit',
    defaultValues: {
      userName: '',
      mailAddress: '',
      loginPassword: '',
      pixtagRiyoFlg: false,
      riyoKyotenId: '',
      riyoKahi: '0',
      riyoKengen: '1',
      pixClientUsage: 0,
      pixCameraUsage: 0,
      voiceRecognitionUsage: 0,
      ocrUsage: 0,
      thetaCameraUsage: 0
    }
  })

  // Profile image blob URL management with automatic cleanup
  const { blobUrl: profileImageUrl } = useBlobUrlFromApi(
    // Fetch function for profile image
    isEdit && user?.profileImageId ? (async () => {
      try {
        return await userKanriService.getProfileImage(
          user.pixUserId,
          user.profileImageId,
          user.filenameExtension
        )
      } catch (error) {
        console.error('Failed to load profile image:', error)
        return null
      }
    }) : null,
    [isEdit, user?.pixUserId, user?.profileImageId, user?.filenameExtension],
    'image/jpeg'
  )

  const loadKyotenList = useCallback(async () => {
    try {
      const response = await userKanriService.getKyotenList()
      
      // Handle business logic errors from the API response
      if (handleApiResponse(response, '拠点リストの読み込みに失敗しました。')) {
        return
      }
      
      setKyotenList(response.kyotenList || [])
    } catch (error) {
      console.error('Failed to load kyoten list:', error)
      handleApiError(error, '拠点リストの読み込みに失敗しました。')
    }
  }, [handleApiResponse, handleApiError])

  const resetForm = useCallback(() => {
    reset({
      userName: '',
      mailAddress: '',
      loginPassword: '',
      pixtagRiyoFlg: false,
      riyoKyotenId: '',
      riyoKahi: '0',
      riyoKengen: '1',
      pixClientUsage: 0,
      pixCameraUsage: 0,
      voiceRecognitionUsage: 0,
      ocrUsage: 0,
      thetaCameraUsage: 0
    })
    setPreviewImageUrl('')
    setSelectedImageFile(null)
    setShowPassword(false)
  }, [reset])

  // Load kyoten list when modal opens
  useEffect(() => {
    if (isOpen) {
      loadKyotenList()
    }
  }, [isOpen, loadKyotenList])

  // Handle form population when user data changes
  useEffect(() => {
    const populateForm = async () => {
      if (isOpen && isEdit && user) {
        // Populate all fields that don't depend on kyoten list
        setValue('userName', user.userName || '')
        setValue('mailAddress', user.mailAddress || '')
        setValue('loginPassword', '')
        setValue('pixtagRiyoFlg', user.pixtagRiyoFlg === '1')
        setValue('riyoKahi', String(user.deleteFlg || 0))
        setValue('riyoKengen', String(user.userAuthority ?? 1))
        setValue('pixClientUsage', user.pixClientUsage ?? 0)
        setValue('pixCameraUsage', user.pixCameraUsage ?? 0)
        setValue('voiceRecognitionUsage', user.voiceRecognitionUsage ?? 0)
        setValue('ocrUsage', user.ocrUsage ?? 0)
        setValue('thetaCameraUsage', user.thetaCameraUsage ?? 0)

        // Only populate riyoKyotenId if kyoten list is available
        if (kyotenList.length > 0) {
          setValue('riyoKyotenId', user.riyoKyotenId || '')
        }

      } else if (isOpen && !isEdit) {
        resetForm()
      }
    }

    populateForm()
  }, [isOpen, isEdit, user, kyotenList, setValue, resetForm])

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      resetForm()
      onClose()
    }
  }

  const handleImageChange = async (event) => {
    const file = event.target.files[0]
    if (file) {
      // Store the selected file for later upload
      setSelectedImageFile(file)
      
      // Create preview URL
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreviewImageUrl(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadProfileImageIfNeeded = async (pixUserId, currentUser = null) => {
    if (!selectedImageFile || !pixUserId) {
      return null
    }

    // Use currentUser parameter if provided, otherwise fall back to user prop
    const userForCache = currentUser || user

    try {
      const formData = new FormData()
      formData.append('inputFile', selectedImageFile)
      formData.append('fileName', selectedImageFile.name)
      formData.append('fileSize', selectedImageFile.size)
      formData.append('pixUserId', String(pixUserId))

      const response = await userKanriService.uploadProfileImage(formData)
      
      if (handleApiResponse(response, 'プロフィール画像のアップロードに失敗しました。')) {
        return null
      }
      
      // Clear ALL cache entries to ensure fresh images are loaded after user list reload
      // This is necessary because refreshKey will change when users array is updated,
      // creating new cache keys, so we need to clear all old cache entries
      clearBlobUrlCache()
      
      // Wait a bit to ensure cache is cleared and server-side processing is complete
      // before reloading user list
      await new Promise(resolve => setTimeout(resolve, 300))
      
      return response
    } catch (error) {
      console.error('Failed to upload profile image:', error)
      handleApiError(error, 'プロフィール画像のアップロードに失敗しました。')
      return null
    }
  }

  const onSubmit = async (data) => {
    // Check user limit based on plan
    const maxUsers = {
      '1': 1,
      '2': 5,
      '3': 30,
      '4': Infinity
    }

    if (userCount >= maxUsers[riyoPlan]) {
      handleApiError(new Error('User limit exceeded'), '追加出来るユーザーの数を超えています。')
      return
    }

    try {
      setIsLoading(true)

      // Check if email already exists (for new users or when email is changed during update)
      if (!isEdit || (isEdit && data.mailAddress !== user?.mailAddress)) {
        const countResponse = await userKanriService.getPixUserCount(data.mailAddress)
        
        // Handle business logic errors from the API response
        if (handleApiResponse(countResponse, 'このメールアドレスは既に登録されています。')) {
          return
        }
        
        // Check if email already exists (business logic check)
        if (countResponse.pixUserCount !== 0) {
          handleApiError(new Error('Email already exists'), 'このメールアドレスは既に登録されています。')
          return
        }
      }

      // Upload profile image first if a new image was selected
      if (selectedImageFile) {
        const pixUserIdForUpload = isEdit ? user?.pixUserId : null
        // For new users, we need to create the user first, then upload the image
        // For existing users, upload the image before updating user data
        if (isEdit && pixUserIdForUpload) {
          const imageResponse = await uploadProfileImageIfNeeded(pixUserIdForUpload, user)
          if (!imageResponse) {
            // Image upload failed, stop here
            return
          }
        }
      }

      const company = companyInfo || {}
      const userData = {
        pixUserId: user?.pixUserId,
        userName: data.userName,
        loginPassword: data.loginPassword || null,
        mailAddress: data.mailAddress,
        pixtagRiyoFlg: data.pixtagRiyoFlg,
        riyoKyotenId: data.riyoKyotenId,
        riyoKahi: data.riyoKahi,
        riyoKengen: data.riyoKengen,
        pixClientUsage: company.pixClientUsage === 1 ? (data.pixClientUsage ?? 0) : 0,
        pixCameraUsage: company.pixCameraUsage === 1 ? (data.pixCameraUsage ?? 0) : 0,
        voiceRecognitionUsage: company.voiceRecognitionUsage === 1 ? (data.voiceRecognitionUsage ?? 0) : 0,
        ocrUsage: company.ocrUsage === 1 ? (data.ocrUsage ?? 0) : 0,
        thetaCameraUsage: company.thetaCameraUsage === 1 ? (data.thetaCameraUsage ?? 0) : 0
      }

      let response
      if (data.riyoKahi === 1) {
        response = await userKanriService.removePixUser(userData)
      } else if (isEdit) {
        response = await userKanriService.updatePixUser(userData)
      } else {
        response = await userKanriService.addPixUser(userData)
      }

      // Handle business logic errors from the API response
      if (handleApiResponse(response)) {
        return
      }

      // For new users, upload profile image after user creation
      if (!isEdit && selectedImageFile && response?.pixUserId) {
        await uploadProfileImageIfNeeded(response.pixUserId)
      }

      // Clear selected image file
      setSelectedImageFile(null)
      setPreviewImageUrl('')

      onSave()
      onClose()
    } catch (error) {
      console.error('Failed to save user:', error)
      handleApiError(error, 'ユーザーの保存に失敗しました。')
    } finally {
      setIsLoading(false)
    }
  }

  const onFormError = (errors) => {
    console.log('Form validation errors:', errors)
  }

  // Simple utility to get border class based on error state
  const getBorderClass = (fieldName, baseClass) => {
    if (errors[fieldName]) {
      return `${baseClass} border border-red-500 bg-red-50`
    }
    return `${baseClass} border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleBackdropClick}>
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800">
            {isEdit ? 'ユーザー編集' : 'ユーザー追加'}
          </h3>
          <button
            onClick={() => {
              resetForm()
              onClose()
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleFormSubmit(onSubmit, onFormError)} className="space-y-6">
          {/* Profile Image Section */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <label
                htmlFor="profile-image-input"
                className="w-24 h-24 border-2 border-gray-300 rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 block"
              >
                {previewImageUrl || profileImageUrl ? (
                  <img
                    src={previewImageUrl || profileImageUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-400 text-sm">画像なし</span>
                  </div>
                )}
              </label>
              <input
                ref={fileInputRef}
                id="profile-image-input"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                aria-label="プロフィール画像を選択"
              />
            </div>
            <span className="text-sm text-gray-600">画像クリックで画像設定可能</span>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-2">
                ユーザー名 *
              </label>
              <input
                id="userName"
                type="text"
                {...register('userName')}
                className={getBorderClass('userName', 'w-full px-3 py-2 rounded-lg')}
                placeholder="ユーザー名を入力してください"
              />
              <p className="mt-1 text-sm text-red-600">{errors.userName ? errors.userName.message : '\u00A0'}</p>
            </div>

            <div>
              <label htmlFor="mailAddress" className="block text-sm font-medium text-gray-700 mb-2">
                メールアドレス *
              </label>
              <input
                id="mailAddress"
                type="email"
                {...register('mailAddress')}
                className={getBorderClass('mailAddress', 'w-full px-3 py-2 rounded-lg')}
                placeholder="メールアドレスを入力してください"
              />
              <p className="mt-1 text-sm text-red-600">{errors.mailAddress ? errors.mailAddress.message : '\u00A0'}</p>
            </div>

            <div>
              <label htmlFor="loginPassword" className="block text-sm font-medium text-gray-700 mb-2">
                パスワード {!isEdit && '*'}
              </label>
              <div className="relative">
                <input
                  id="loginPassword"
                  type={showPassword ? 'text' : 'password'}
                  {...register('loginPassword')}
                  className={getBorderClass('loginPassword', 'w-full px-3 py-2 pr-10 rounded-lg')}
                  placeholder={!isEdit ? "パスワードを入力してください" : "変更する場合のみ入力"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? "パスワードを隠す" : "パスワードを表示"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1 text-sm text-red-600">{errors.loginPassword ? errors.loginPassword.message : '\u00A0'}</p>
            </div>

            <div>
              <label htmlFor="pixtagRiyoFlg" className="flex items-center space-x-2">
                <input
                  id="pixtagRiyoFlg"
                  type="checkbox"
                  {...register('pixtagRiyoFlg')}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">pix-label利用</span>
              </label>
            </div>

            <div>
              <label htmlFor="riyoKyotenId" className="block text-sm font-medium text-gray-700 mb-2">
                利用拠点
              </label>
              <select
                id="riyoKyotenId"
                {...register('riyoKyotenId')}
                className={getBorderClass('riyoKyotenId', 'w-full px-3 py-2 rounded-lg')}
              >
                <option value="">----</option>
                {kyotenList.map((kyoten) => (
                  <option key={kyoten.kyotenId} value={kyoten.kyotenId}>
                    {kyoten.kyotenName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="riyoKahi" className="block text-sm font-medium text-gray-700 mb-2">
                利用可否
              </label>
              <select
                id="riyoKahi"
                {...register('riyoKahi')}
                className={getBorderClass('riyoKahi', 'w-full px-3 py-2 rounded-lg')}
              >
                <option value={0}>利用可能</option>
                <option value={1}>利用不可</option>
              </select>
            </div>

            <div>
              <label htmlFor="riyoKengen" className="block text-sm font-medium text-gray-700 mb-2">
                利用権限
              </label>
              <select
                id="riyoKengen"
                {...register('riyoKengen')}
                className={getBorderClass('riyoKengen', 'w-full px-3 py-2 rounded-lg')}
              >
                <option value={0}>マスター管理者</option>
                <option value={1}>出品可能</option>
                <option value={2}>出品不可</option>
              </select>
            </div>

            {/* 追加機能 - show checkbox only when company has the feature */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">追加機能</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className={`flex items-center gap-2 ${companyInfo?.pixClientUsage !== 1 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={watch('pixClientUsage') === 1}
                    onChange={(e) => companyInfo?.pixClientUsage === 1 && setValue('pixClientUsage', e.target.checked ? 1 : 0)}
                    disabled={companyInfo?.pixClientUsage !== 1}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">PIX Client{companyInfo?.pixClientUsage !== 1 ? ' (利用不可)' : ''}</span>
                </label>
                <label className={`flex items-center gap-2 ${companyInfo?.pixCameraUsage !== 1 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={watch('pixCameraUsage') === 1}
                    onChange={(e) => companyInfo?.pixCameraUsage === 1 && setValue('pixCameraUsage', e.target.checked ? 1 : 0)}
                    disabled={companyInfo?.pixCameraUsage !== 1}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">PIX カメラ{companyInfo?.pixCameraUsage !== 1 ? ' (利用不可)' : ''}</span>
                </label>
                <label className={`flex items-center gap-2 ${companyInfo?.voiceRecognitionUsage !== 1 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={watch('voiceRecognitionUsage') === 1}
                    onChange={(e) => companyInfo?.voiceRecognitionUsage === 1 && setValue('voiceRecognitionUsage', e.target.checked ? 1 : 0)}
                    disabled={companyInfo?.voiceRecognitionUsage !== 1}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">音声認識{companyInfo?.voiceRecognitionUsage !== 1 ? ' (利用不可)' : ''}</span>
                </label>
                <label className={`flex items-center gap-2 ${companyInfo?.ocrUsage !== 1 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={watch('ocrUsage') === 1}
                    onChange={(e) => companyInfo?.ocrUsage === 1 && setValue('ocrUsage', e.target.checked ? 1 : 0)}
                    disabled={companyInfo?.ocrUsage !== 1}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">OCR{companyInfo?.ocrUsage !== 1 ? ' (利用不可)' : ''}</span>
                </label>
                <label className={`flex items-center gap-2 ${companyInfo?.thetaCameraUsage !== 1 ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={watch('thetaCameraUsage') === 1}
                    onChange={(e) => companyInfo?.thetaCameraUsage === 1 && setValue('thetaCameraUsage', e.target.checked ? 1 : 0)}
                    disabled={companyInfo?.thetaCameraUsage !== 1}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">THETA カメラ{companyInfo?.thetaCameraUsage !== 1 ? ' (利用不可)' : ''}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={() => {
                resetForm()
                onClose()
              }}
              variant="outline"
              size="md"
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={isLoading}
            >
              {isEdit ? '更新' : '登録'}
            </Button>
          </div>
        </form>
      </div>

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        title="エラー"
        message={errorMessage}
        type="error"
        onClose={closeErrorDialog}
      />
    </div>
  )
}

export default UserModal
